import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, pipe, tap, catchError, of } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { setError, setFulfilled, setPending, withRequestStatus } from '@/shared/state/request-status.feature';
import { AuthenticationService, CreateUserDto, LoginDto, UserDto, UsersService } from '@/api-backend/src';
import { extractErrorMessage } from '@/shared/utils/error-handler.util';
import status = UserDto.status;
import role = UserDto.role;

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<UserDto>({
    created_at: '',
    email: '',
    first_name: '',
    id: '',
    last_name: '',
    password_hash: '',
    phone: '',
    role: role.SUPPORTER,
    status: status.ACTIVE,
    updated_at: '',
    solicitor: undefined,
    charity: undefined,
  }),
  withDevtools('auth'),
  withRequestStatus(),
  withComputed(store => ({
    isAuthenticated: computed(() => {
      const token = localStorage.getItem('restful-token');
      if (!token) return false;
      try {
        const jwtService = inject(JwtHelperService);
        return !jwtService.isTokenExpired(token);
      } catch (error) {
        console.log('Error checking token expiration:', error);
        return false;
      }
    }),
    role: computed(() => store.role()),
  })),
  withMethods(
    (
      store,
      userService = inject(UsersService),
      authenticationService = inject(AuthenticationService),
      jwtService = inject(JwtHelperService),
      snackbar = inject(MatSnackBar),
      router = inject(Router)
    ) => ({
      register: rxMethod<{
        email: string;
        first_name: string;
        last_name: string;
        phone: string;
        role: CreateUserDto.role;
        status: UserDto.status;
        password: string;
      }>(
        pipe(
          tap(() => {
            patchState(store, setPending());
          }),
          exhaustMap(({ email, first_name, last_name, phone, role, password }) => {
            return userService
              .usersControllerSignup({
                email,
                status: CreateUserDto.status.PENDING,
                first_name,
                last_name,
                phone,
                role,
                password,
              })
              .pipe(
                tap(user => {
                  snackbar.open('User registered', 'Close', {
                    duration: 2000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  router.navigate(['user/login']);
                  patchState(store, user, setFulfilled());
                }),
                catchError((error: any) => {
                  const errorMsg = extractErrorMessage(error, 'Registration failed');
                  snackbar.open(errorMsg, 'Close', {
                    duration: 2000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  patchState(store, setError(errorMsg));
                  return of(null);
                })
              );
          })
        )
      ),
      login: rxMethod<{ loginDto: LoginDto }>(
        pipe(
          exhaustMap(({ loginDto }) => {
            return authenticationService
              .authControllerLogin({ email: loginDto.email, password: loginDto.password })
              .pipe(
                tap(loginResponseDto => {
                  localStorage.setItem('restful-token', loginResponseDto.access_token);
                  console.log('Login response user:', loginResponseDto.user);
                  patchState(
                    store,
                    {
                      ...loginResponseDto.user,
                      id: loginResponseDto.user.id || '',
                      last_login_at: new Date().toISOString(),
                    },
                    setFulfilled()
                  );
                  snackbar.open('Login Successful', 'Close', {
                    duration: 4000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  // Redirect based on user role
                  if (loginResponseDto.user.role === role.SOLICITOR) {
                    router.navigate(['admin/solicitor-will-requests']);
                  } else if (loginResponseDto.user.role === role.CHARITY) {
                    router.navigate(['admin/charity-will-requests']);
                  } else {
                    router.navigate(['admin/solicitors']);
                  }
                }),
                catchError((error: any) => {
                  const errorMsg = extractErrorMessage(error, 'Login failed');
                  snackbar.open(errorMsg, 'Close', {
                    duration: 4000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  patchState(store, setError(errorMsg));
                  return of(null);
                })
              );
          })
        )
      ),
      getProfile: rxMethod(
        pipe(
          exhaustMap(() => {
            return authenticationService.authControllerGetProfile().pipe(
              tap(userDto => {
                console.log('Profile response user:', userDto);
                patchState(
                  store,
                  {
                    ...userDto,
                    id: userDto.id || '',
                  },
                  setFulfilled()
                );
              }),
              catchError((error: any) => {
                const errorMsg = extractErrorMessage(error, 'Failed to get profile');
                snackbar.open(errorMsg, 'Close', {
                  duration: 4000,
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                });
                patchState(store, setError(errorMsg));
                return of(null);
              })
            );
          })
        )
      ),
      autoLogin() {
        try {
          const token = localStorage.getItem('restful-token');
          if (!token) {
            return; // No token, user is not logged in
          }
          const expired = jwtService.isTokenExpired(token);
          console.log('Token expired:', expired);
          if (!expired) {
            this.getProfile({});
          } else {
            this.logout();
          }
        } catch (error) {
          console.log('Error checking token expiration:', error);
          this.logout(); // Clear any invalid token
        }
      },
      logout() {
        // Get current role before clearing state
        const currentRole = store.role();

        localStorage.removeItem('restful-token');
        console.log('logged out');
        patchState(store, {
          created_at: '',
          email: '',
          first_name: '',
          id: '',
          last_name: '',
          password_hash: '',
          phone: '',
          role: role.SUPPORTER,
          status: status.ACTIVE,
          updated_at: '',
          solicitor: undefined,
        }); // Clear user state

        // Redirect based on role
        if (currentRole === role.SOLICITOR) {
          router.navigate(['/solicitor']);
        } else if (currentRole === role.SUPER_ADMIN) {
          router.navigate(['/admin']);
        } else {
          router.navigate(['/']); // Default to charity landing page
        }
      },
      getUserInfo() {
        const { email, first_name, last_name } = store;
        if (email()) {
          return {
            email: email(),
            first_name: first_name(),
            last_name: last_name(),
          };
        }
        return null;
      },
    })
  ),
  withHooks({
    onInit(store) {
      store.autoLogin();
    },
  })
);
